import * as MuiIcons from '@material-ui/icons';

import { PREDEFINED_CONDITIONS } from './definitions';
import {
  Action,
  ActionByKeyEntry,
  Argument,
  DISPLAY_CONTEXT_ALL,
  DISPLAY_CONTEXT_OPTIONS,
  DisplayContext,
  FunctionDefinition,
  IconComponentType,
  IconOption,
  IconSource,
  Milestone,
} from './types';
import { CUSTOM_ICON_REGISTRY } from './nba.constants';

export const actionKey = (milestoneName: string, stepName: string, actionName: string) =>
  `${milestoneName}||${stepName}||${actionName}`;

export const buildDefaultArgs = (func?: FunctionDefinition | null) => {
  const defaults: Record<string, any> = {};
  if (!func?.args?.length) return defaults;

  for (const arg of func.args) {
    if (arg.default !== undefined) {
      defaults[arg.name] = arg.default;
    }
  }

  return defaults;
};

export type ArgumentValidationResult =
  | { ok: true; value?: unknown }
  | { ok: false; message: string };

export type ArgumentValidator = (value: unknown, arg: Argument) => ArgumentValidationResult;

export const ARGUMENT_TYPE_VALIDATORS: Partial<Record<Argument['type'], ArgumentValidator>> = {
  url: (value: unknown, arg: Argument): ArgumentValidationResult => {
    const raw = typeof value === 'string' ? value : String(value ?? '');

    const trimmed = raw.trim();
    if (!trimmed) {
      return { ok: true, value: '' };
    }

    if (/\s/.test(trimmed) || trimmed.includes('\\')) {
      return { ok: false, message: `Argument "${arg.name}" must not contain spaces or backslashes.` };
    }

    if (trimmed.startsWith('/')) {
      if (trimmed.startsWith('//')) {
        return { ok: false, message: `Argument "${arg.name}" must start with "/" or "https://".` };
      }

      return { ok: true, value: trimmed };
    }

    if (!/^https:\/\//i.test(trimmed)) {
      return { ok: false, message: `Argument "${arg.name}" must start with "/" or "https://".` };
    }

    try {
      const parsed = new URL(trimmed);
      if (parsed.protocol !== 'https:' || !parsed.hostname || parsed.username || parsed.password) {
        return { ok: false, message: `Argument "${arg.name}" must be a valid https URL.` };
      }

      return { ok: true, value: trimmed };
    } catch {
      return { ok: false, message: `Argument "${arg.name}" must be a valid https URL.` };
    }
  },
};

const isEmptyArgValue = (value: unknown) =>
  value === undefined
  || value === null
  || (typeof value === 'string' && value.trim() === '');

export const validateAndNormalizeArgs = (
  func?: FunctionDefinition | null,
  args?: Record<string, any>,
): { ok: true; args: Record<string, any> } | { ok: false; message: string } => {
  if (!func?.args?.length) {
    return { ok: true, args: args || {} };
  }

  const nextArgs: Record<string, any> = { ...(args || {}) };

  for (const arg of func.args) {
    const value = nextArgs[arg.name];

    if (arg.required && isEmptyArgValue(value)) {
      return { ok: false, message: `Argument "${arg.name}" is required.` };
    }

    if (isEmptyArgValue(value)) {
      continue;
    }

    const validator = ARGUMENT_TYPE_VALIDATORS[arg.type];
    if (!validator) {
      continue;
    }

    const result = validator(value, arg);
    if (!result.ok) {
      return { ok: false, message: result.message };
    }

    if (result.value !== undefined) {
      nextArgs[arg.name] = result.value;
    }
  }

  return { ok: true, args: nextArgs };
};

const MILESTONE_KEY_PREFIX = 'milestone||';

export const formMilestoneKey = (milestoneName: string) => `${MILESTONE_KEY_PREFIX}${milestoneName}`;
export const isMilestoneKey = (key: string) => key.startsWith(MILESTONE_KEY_PREFIX);
export const getMilestoneNameFromMilestoneKey = (key: string) => key.replace(MILESTONE_KEY_PREFIX, '');

export const buildActionByKey = (milestones: Milestone[]) => {
  const map = new Map<string, ActionByKeyEntry>();

  for (const milestone of milestones) {
    const { steps, name: milestoneName } = milestone;

    for (const step of steps) {
      const { actions, name: stepName } = step;

      for (const action of actions) {
        const { name: actionName } = action;

        map.set(actionKey(milestoneName, stepName, actionName), {
          milestoneName,
          stepName,
          action,
        });
      }
    }
  }

  return map;
};

export const generateUniqueId = (): string => {
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2);
  return `${timestamp}-${randomPart}`;
};

export const addIds = (data: any[]) => {
  return data.map((milestone: any) => ({
    ...milestone,
    id: milestone._id,
    clientId: generateUniqueId(),
    steps: milestone.steps.map((step: any) => ({
      ...step,
      id: step._id,
      clientId: generateUniqueId(),
      actions: step.actions.map((action: any) => ({
        ...action,
        id: action._id,
        clientId: generateUniqueId(),
      })),
    })),
  }));
};

export const stripClientIds = (item: any): any => {
  if (Array.isArray(item)) {
    return item.map(stripClientIds);
  }

  if (typeof item === 'object' && item !== null) {
    const { id, clientId, ...rest } = item;

    for (const key in rest) {
      rest[key] = stripClientIds(rest[key]);
    }

    return rest;
  }

  return item;
};

export const getDisplayContextLabel = (value?: DisplayContext[]): string => {
  const defaultLabel =
    DISPLAY_CONTEXT_OPTIONS.find(option => option.value === DISPLAY_CONTEXT_ALL)?.label
    ?? DISPLAY_CONTEXT_OPTIONS[0]?.label
    ?? '';

  if (!value || value.length === 0) {
    return defaultLabel;
  }

  const labelMap = new Map<DisplayContext, string>(
    DISPLAY_CONTEXT_OPTIONS.map(option => [option.value, option.label] as const),
  );

  const labels = value
    .map(v => labelMap.get(v))
    .filter((label): label is string => Boolean(label));

  return labels.length > 0 ? labels.join(', ') : defaultLabel;
};

export const formatActionConditions = (conditions: Action['conditions']) => {
  if (!conditions || Object.keys(conditions).length === 0) {
    return 'None';
  }

  const parts = PREDEFINED_CONDITIONS.map((def) => {
    const value = (conditions as Record<string, any>)[def.key];

    if (value === undefined || value === null) {
      return null;
    }

    if (!def.formatSummary) {
      return `${def.label}: ${String(value)}`;
    }

    const summary = def.formatSummary(value);
    if (!summary) {
      return null;
    }

    return `${def.label}: ${summary}`;
  }).filter(Boolean) as string[];

  return parts.length ? parts.join('; ') : 'None';
};

const buildIconOptions = (): IconOption[] => {
  const customOptions: IconOption[] = Object.entries(CUSTOM_ICON_REGISTRY).map(
    ([name, Icon]) => ({
      id: `${IconSource.CUSTOM}:${name}`,
      label: `Custom: ${name}`,
      source: IconSource.CUSTOM,
      Icon: Icon as IconComponentType,
    })
  );

  const muiOptions: IconOption[] = Object.keys(MuiIcons)
    .filter((key) => {
      const value = (MuiIcons as any)[key];
      return typeof value === 'object' && value && 'type' in value;
    })
    .sort()
    .map((key) => {
      const Icon = (MuiIcons as any)[key] as IconComponentType;
      return {
        id: `${IconSource.MUI}:${key}`,
        label: key,
        source: IconSource.MUI,
        Icon,
      };
    });

  return [...customOptions, ...muiOptions];
};

export const ICON_OPTIONS: IconOption[] = buildIconOptions();

export const findIconOptionById = (id?: string | null): IconOption | null => {
  if (!id) {
    return null;
  }

  return ICON_OPTIONS.find((opt) => opt.id === id) || null;
};

export const resolveMilestoneIcon = (
  iconId?: string | null
): IconComponentType | null => {
  if (!iconId) {
    return null;
  }

  const [source, name] = iconId.split(':');
  if (!name) {
    return null;
  }

  if (source === IconSource.MUI) {
    const Icon = (MuiIcons as any)[name] as IconComponentType | undefined;
    return Icon || null;
  }

  if (source === IconSource.CUSTOM) {
    const Icon = CUSTOM_ICON_REGISTRY[name] as IconComponentType | undefined;
    return Icon || null;
  }

  return null;
};

export const generateUniqueCopyName = (baseName: string, existingNames: Set<string>): string => {
  let newName = `${baseName} (Copy)`;
  let counter = 1;

  while (existingNames.has(newName)) {
    counter++;
    newName = `${baseName} (Copy ${counter})`;
  }

  return newName;
};

export const cleanForDuplication = (obj: unknown): unknown => {
  if (Array.isArray(obj)) {
    return obj.map(cleanForDuplication);
  }

  if (typeof obj === 'object' && obj !== null) {
    const { _id, id, clientId, ...rest } = obj as Record<string, unknown>;

    const cleanedRest: Record<string, unknown> = {};
    for (const key in rest) {
      cleanedRest[key] = cleanForDuplication(rest[key]);
    }

    return cleanedRest;
  }

  return obj;
};
